/**
 * design.md §3 — the browser tab names the page, most specific first, then the
 * product:
 *
 *   Sunrise Public School Jaipur · Payments · Billing · SB Tools
 *
 * It is the breadcrumb read backwards, so a page states its place once (`trail`) and
 * both follow. Names come from the app documents and the product name from the
 * server's `APP_NAME`; nothing here knows a particular app.
 */

import { useQuery } from '@tanstack/react-query'
import * as React from 'react'

import { api } from '@/lib/api'
import type { AuthConfig } from '@/lib/types'

/** index.html's own title: what the tab says before the server has answered. */
const FALLBACK = document.title

/** Sign-in settings and the product name. Public, and the same for everyone. */
export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/api/auth/config'),
    staleTime: Infinity,
  })
}

export function useProductName(): string {
  return useAuthConfig().data?.product_name || FALLBACK
}

export function pageTitle(trail: readonly string[], product: string): string {
  const parts = [...trail].reverse().filter((part, i, all) => part && part !== all[i - 1])
  return [...parts, product].join(' · ')
}

export function usePageTitle(trail: readonly string[] = []) {
  const title = pageTitle(trail, useProductName())
  React.useEffect(() => {
    document.title = title
  }, [title])
}
